/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {IndexedObject, ModelResponse, nullify, ResponseData} from '@dvrd/dvr-controls';
import {isEqual, sortBy} from 'lodash';
import {CustomerType, EmailActivity, EmailFlow, Fulfilment} from '../util/interfaces';
import {
    createEmailStep, deleteEmailStep, getAdminEmailSteps, getEmailSteps, updateEmailStep
} from '../util/requestSender';
import {deepClone} from '../util/utils';
import Account from './accountModel';
import Attachment from './attachmentModel';
import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import BolProduct from './bolProductModel';
import EmailTemplate from './emailTemplateModel';
import IDate from '@dvrd/idate';

interface Props extends BaseProps {
    label?: string;
    flow?: EmailFlow;
    customer_type?: CustomerType;
    days?: number;
    template_id?: string;
    product_eans?: string[];
    with_invoice?: boolean;
    countries?: string[];
    active?: boolean;
    num_sent?: number;
    num_open?: number;
    num_click?: number;
    only_work_days?: boolean;
    flex?: IndexedObject<any>;
    account_id?: string;
    account?: Account | any;
    attachments?: (Attachment | any)[];
    fulfilment?: Fulfilment;
    template?: EmailTemplate | any | null;

    // Admin fields
    active_since?: string | null;
    activity?: Array<EmailActivity>;
}

export interface EmailStepFields extends BaseFields {
    label: string;
    flow: EmailFlow;
    customer_type: CustomerType;
    days: number;
    template_id: string;
    product_eans: string[];
    with_invoice: boolean;
    countries: string[];
    active: boolean;
    num_sent: number;
    num_open: number;
    num_click: number;
    only_work_days: boolean;
    flex: IndexedObject<any>;
    account_id: string;
    account: Account | null;
    attachments: Attachment[];
    fulfilment: Fulfilment;
    template: EmailTemplate | null;

    // Admin fields
    active_since: string | null;
    activity: Array<EmailActivity>;
}

const fields: EmailStepFields = {
    id: '',
    created_at: '',
    updated_at: '',
    label: '',
    flow: EmailFlow.INVOICE,
    customer_type: CustomerType.BOTH,
    days: 0,
    template_id: '',
    product_eans: [],
    with_invoice: true,
    countries: [],
    active: true,
    num_sent: 0,
    num_open: 0,
    num_click: 0,
    only_work_days: false,
    account_id: '',
    account: null,
    attachments: [],
    fulfilment: Fulfilment.BOTH,
    template: null,
    flex: {},

    // Admin fields
    active_since: null,
    activity: []
};

const fieldsDefinition: { [key in keyof Omit<EmailStepFields, 'account' | 'attachments' | 'template'>]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    label: createFieldDef(ModelFieldType.STRING),
    days: createFieldDef(ModelFieldType.NUMBER),
    template_id: createFieldDef(ModelFieldType.STRING),
    with_invoice: createFieldDef(ModelFieldType.BOOLEAN),
    active: createFieldDef(ModelFieldType.BOOLEAN),
    num_sent: createFieldDef(ModelFieldType.NUMBER),
    num_click: createFieldDef(ModelFieldType.NUMBER),
    num_open: createFieldDef(ModelFieldType.NUMBER),
    account_id: createFieldDef(ModelFieldType.STRING),
    customer_type: createFieldDef(ModelFieldType.STRING, CustomerType.BOTH),
    flow: createFieldDef(ModelFieldType.STRING, EmailFlow.INVOICE),
    product_eans: createFieldDef(ModelFieldType.ARRAY),
    only_work_days: createFieldDef(ModelFieldType.BOOLEAN),
    countries: createFieldDef(ModelFieldType.ARRAY),
    fulfilment: createFieldDef(ModelFieldType.STRING, Fulfilment.BOTH),
    flex: createFieldDef(ModelFieldType.OBJECT),
    active_since: createFieldDef(ModelFieldType.STRING, null, true),
    activity: createFieldDef(ModelFieldType.ARRAY)
};

const ignoreProps: Array<keyof Props> = ['account', 'attachments'];

export default class EmailStep extends BaseModel<Props, EmailStepFields> {
    static getAll = (accountId: string, search: string | null, callback: ModelResponse<EmailStep[]>) => {
        getEmailSteps({
            data: {account_id: accountId, search},
            callback: (data: ResponseData) => {
                const steps: EmailStep[] = data.success ? data.email_steps.map((step: any) => new EmailStep(step)) : [];
                callback(steps, data.success, data);
            }
        });
    };

    static getAdminAll = (userId: string, accountId: string, callback: ModelResponse<Array<EmailStep>>) => {
        getAdminEmailSteps({
            userId, accountId, callback: (data: ResponseData) => {
                const steps: EmailStep[] = data.success ? data.email_steps.map((step: any) => new EmailStep(step)) : [];
                callback(steps, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get label(): string {
        return this.fields.label;
    }

    set label(value: string) {
        this.fields.label = value;
    }

    get flow(): EmailFlow {
        return this.fields.flow;
    }

    set flow(flow: EmailFlow) {
        this.fields.flow = flow;
    }

    get customerType(): CustomerType {
        return this.fields.customer_type;
    }

    set customerType(customer_type: CustomerType) {
        this.fields.customer_type = customer_type;
    }

    get days(): number {
        return this.fields.days;
    }

    set days(days: number) {
        this.fields.days = days;
    }

    get translatedDays(): string {
        const {days} = this;
        if (days === -1) return 'Order geplaatst';
        if (days === 0) return 'Order verzonden';
        if (days === 1) return '1 dag na zending';
        return `${days} dagen na zending`;
    }

    get templateId(): string {
        return this.fields.template_id;
    }

    set templateId(template_id: string) {
        this.fields.template_id = template_id;
    }

    get productEans(): string[] {
        return this.fields.product_eans;
    }

    set productEans(product_eans: string[]) {
        this.fields.product_eans = product_eans;
    }

    get withInvoice(): boolean {
        return this.fields.with_invoice;
    }

    set withInvoice(with_invoice: boolean) {
        this.fields.with_invoice = with_invoice;
    }

    get active(): boolean {
        return this.fields.active;
    }

    set active(active: boolean) {
        this.fields.active = active;
    }

    get numSent(): number {
        return this.fields.num_sent;
    }

    set numSent(num_sent: number) {
        this.fields.num_sent = num_sent;
    }

    get numOpen(): number {
        return this.fields.num_open;
    }

    get numClick(): number {
        return this.fields.num_click;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get account(): Account | null {
        return this.fields.account;
    }

    set account(account: Account | null) {
        this.fields.account = account;
        if (account) this.fields.account_id = account.id;
    }

    get attachments(): Attachment[] {
        return this.fields.attachments;
    }

    set attachments(attachments: Attachment[]) {
        this.fields.attachments = attachments;
    }

    get onlyWorkDays(): boolean {
        return this.fields.only_work_days;
    }

    set onlyWorkDays(only_work_days: boolean) {
        this.fields.only_work_days = only_work_days;
    }

    get countries(): string[] {
        return this.fields.countries;
    }

    set countries(countries: string[]) {
        this.fields.countries = countries;
    }

    get fulfilment(): Fulfilment {
        return this.fields.fulfilment;
    }

    set fulfilment(fulfilment: Fulfilment) {
        this.fields.fulfilment = fulfilment;
    }

    get template(): EmailTemplate | null {
        return this.fields.template;
    }

    get flex(): IndexedObject<any> {
        return this.fields.flex;
    }

    set flex(flex: IndexedObject<any>) {
        this.fields.flex = flex;
    }

    get activeSinceMoment(): IDate | null {
        if (!this.fields.active_since) return null;
        return new IDate(this.fields.active_since);
    }

    get activity(): Array<EmailActivity> {
        return this.fields.activity;
    }

    clone = (deep: boolean = true): EmailStep => {
        let fields = this.fields;
        if (deep) fields = deepClone(fields);
        return new EmailStep(fields);
    };

    getFlex = (key: string, defaultValue: any = null): any => {
        return this.fields.flex[key] ?? defaultValue;
    };

    setFlex = (key: string, value: any) => {
        this.fields.flex[key] = value;
    };

    deleteFlex = (key: string) => {
        delete this.fields.flex[key];
    };

    addAttachment = (attachment: Attachment): EmailStep => {
        this.fields.attachments = this.fields.attachments.concat([attachment]);
        return this;
    };

    removeAttachment = (attachment: Attachment): EmailStep => {
        this.fields.attachments = this.fields.attachments.filter((att: Attachment) => att.id !== attachment.id);
        return this;
    };

    hasAttachment = (attachment: Attachment): boolean => {
        return this.fields.attachments.find((att: Attachment) => att.id === attachment.id) !== undefined;
    };

    addProduct = (product: string | BolProduct): EmailStep => {
        const ean = typeof product === 'string' ? product : product.ean;
        this.fields.product_eans.push(ean);
        return this;
    };

    removeProduct = (product: string | BolProduct): EmailStep => {
        const ean = typeof product === 'string' ? product : product.ean;
        this.fields.product_eans = this.fields.product_eans.filter((eanNumber: string) => eanNumber !== ean);
        return this;
    };

    hasProduct = (product: BolProduct | string): boolean => {
        const ean = typeof product === 'string' ? product : product.ean;
        return this.fields.product_eans.includes(ean);
    };

    save = (callback?: ModelResponse<EmailStep>) => {
        createEmailStep({
            data: {
                account_id: this.accountId,
                label: this.label,
                active: this.active,
                days: this.days,
                flow: this.flow,
                product_eans: nullify(this.productEans),
                template_id: nullify(this.templateId),
                with_invoice: this.withInvoice,
                attachment_ids: nullify(this.attachments.map((att: Attachment) => att.id)),
                customer_type: this.customerType,
                only_work_days: this.onlyWorkDays,
                countries: nullify(this.countries),
                fulfilment: this.fulfilment
            }, callback: (data: ResponseData) => {
                if (data.success) this.build(data.email_step);
                if (callback) callback(this, data.success, data);
            }
        });
    };

    update = (callback?: ModelResponse<EmailStep>) => {
        updateEmailStep({
            data: {
                label: this.label,
                customer_type: this.customerType,
                product_eans: nullify(this.productEans),
                days: this.days,
                flow: this.flow,
                attachment_ids: nullify(this.attachments.map((att: Attachment) => att.id)),
                with_invoice: this.withInvoice,
                email_step_id: this.id,
                template_id: nullify(this.templateId),
                active: this.active,
                account_id: this.accountId,
                only_work_days: this.onlyWorkDays,
                countries: nullify(this.countries),
                fulfilment: this.fulfilment
            }, callback: (data: ResponseData) => {
                if (data.success) this.build(data.email_step);
                if (callback) callback(this, data.success, data);
            }
        });
    };

    delete = (callback?: ModelResponse<EmailStep>) => {
        deleteEmailStep({
            email_step_id: this.id, account_id: this.accountId, callback: (data: ResponseData) => {
                if (callback) callback(this, data.success, data);
            }
        });
    };

    eansDiffer = (newEANs: Array<string>) => {
        return !isEqual(sortBy(this.productEans), sortBy(newEANs));
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.account) {
                this.account = props.account instanceof Account ? props.account : new Account(props.account);
            }
            if (props.attachments) {
                this.fields.attachments = props.attachments.map(
                    (att: any) => att instanceof Attachment ? att : new Attachment(att));
            }
            if (props.template) {
                this.fields.template = props.template instanceof EmailTemplate ? props.template :
                    new EmailTemplate(props.template);
            }
        }
    };
}